/**
 * The messages of a scoped agent chat: restore spinner, the rows, the
 * "thinking" and "someone else is talking" indicators, and the scroll-to-
 * bottom button. Width and padding are the caller's (`className`,
 * `contentClassName`), so the side panel and the workspace share the body.
 */
import { Loader2 } from "lucide-react";
import { useCallback, type FC, type ReactNode } from "react";

import {
  Conversation,
  ConversationContent,
  ConversationScrollButton
} from "@/components/ai-elements/conversation";
import { BrandLoader } from "@/components/brand";
import { ChatMessage } from "@/features/caseIde/chat/components/ChatMessage";
import { opensReplyTurn } from "@/features/caseIde/chat/components/replyTurn";
import { TurnStatusLine } from "@/features/caseIde/chat/components/TurnStatusLine";
import { OfferActionsProvider } from "@/features/caseIde/chat/offers/OfferActionsContext";
import { RunEventsProvider } from "@/features/caseIde/chat/runEvents";
import {
  SupportCardsProvider,
  useSupportCards
} from "@/features/caseIde/chat/support/SupportCardsContext";
import { cn } from "@/lib/utils";

import { AttachmentScopeProvider } from "../attachments/AttachmentScopeContext";
import { activeTurnCopy, type AgentUIMessage } from "../useAgentChat";
import type { ScopedAgentChat } from "../useScopedAgentChat";

/** Older replies skip layout and paint until scrolled back into view. */
const SETTLED_TAIL = 6;

type Props = {
  scoped: ScopedAgentChat;
  className?: string;
  contentClassName?: string;
  /** Per-message actions row (copy, retry, open as report) for assistant messages. */
  messageActions?: (message: AgentUIMessage, isLast: boolean) => ReactNode;
  /** Hide which model wrote each reply (the library chat). */
  hideModel?: boolean;
  /** Under each finished reply, above its footer (the library chat's sources). */
  replyAppendix?: (message: AgentUIMessage) => ReactNode;
};

export const AgentTranscript: FC<Props> = ({
  scoped,
  className,
  contentClassName,
  messageActions,
  hideModel,
  replyAppendix
}) => {
  const {
    chat,
    me,
    label,
    highlightedId,
    onApproval,
    onAnswer,
    onConnectResolved,
    offerActions,
    streaming,
    lastAssistantId,
    otherTurn
  } = scoped;
  // The reply being streamed: the last assistant message, once it exists and
  // nothing (a user message) has landed after it.
  const lastMessage = chat.messages[chat.messages.length - 1];
  const streamingMessage =
    streaming && lastMessage && lastMessage.id === lastAssistantId
      ? lastMessage
      : null;
  const settledBefore = Math.max(0, chat.messages.length - SETTLED_TAIL);
  const supportCards = useSupportCards(chat);
  const onRetry = useCallback(
    (options?: { model?: string }) =>
      void chat.regenerate(
        options?.model ? { body: { model: options.model } } : undefined
      ),
    [chat]
  );
  const onStop = useCallback(() => void chat.stopTurn(), [chat]);

  if (chat.restoring) {
    return (
      <div
        className={cn(
          "flex min-h-0 flex-1 items-center justify-center p-4",
          className
        )}
      >
        <BrandLoader size="md" label="Restoring conversation" />
      </div>
    );
  }

  return (
    <Conversation className={cn("min-h-0 flex-1", className)}>
      <ConversationContent className={cn("gap-4 p-4", contentClassName)}>
        <AttachmentScopeProvider scope={chat.scope}>
          <RunEventsProvider messages={chat.messages}>
            <OfferActionsProvider value={offerActions}>
              <SupportCardsProvider value={supportCards}>
                {chat.messages.map((m, i) => {
                  const isLast = m.id === lastAssistantId;
                  const isStreamingReply = streamingMessage?.id === m.id;
                  return (
                    <ChatMessage
                      key={m.id}
                      message={m}
                      isStreaming={isStreamingReply}
                      onApproval={onApproval}
                      onAnswer={onAnswer}
                      onConnectResolved={onConnectResolved}
                      meUid={me.uid || null}
                      meAvatarUrl={me.avatarUrl}
                      highlighted={highlightedId === m.id}
                      steps={isStreamingReply ? chat.turnSteps : undefined}
                      onRetry={isLast && !streaming ? onRetry : undefined}
                      settled={i < settledBefore && highlightedId !== m.id}
                      showByline={opensReplyTurn(chat.messages, i)}
                      hideModel={hideModel}
                      replyAppendix={replyAppendix}
                      actions={
                        messageActions &&
                        m.role === "assistant" &&
                        !(streaming && isLast)
                          ? messageActions(m, isLast)
                          : undefined
                      }
                    />
                  );
                })}
              </SupportCardsProvider>
            </OfferActionsProvider>
          </RunEventsProvider>
        </AttachmentScopeProvider>
        {streaming ? (
          <TurnStatusLine message={streamingMessage} onStop={onStop} />
        ) : null}
        {otherTurn ? (
          <div
            className="flex items-center gap-2 text-xs text-muted-foreground"
            data-testid="other-turn"
          >
            <Loader2 className="size-3 animate-spin" />
            {activeTurnCopy(otherTurn, label, me.uid)}…
          </div>
        ) : null}
      </ConversationContent>
      <ConversationScrollButton />
    </Conversation>
  );
};
